import { AgFilterComponent } from '@ag-grid-community/angular';
import { NgIf } from '@angular/common';
import { IDoesFilterPassParams, IFilterParams, IRowNode } from '@ag-grid-community/core';
import { ChangeDetectionStrategy, Component, HostBinding, OnDestroy, ViewEncapsulation } from '@angular/core';
import { FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';

import { FilterType } from '../../models/ag-grid';

@Component({
	selector: 'copo-datacenter-exact-number-filter',
	templateUrl: './datacenter-exact-number-filter.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		TranslateModule,
		MatFormFieldModule,
		MatInputModule,
		ReactiveFormsModule,
		MatIconModule,
		NgIf
	]
})
export class DatacenterExactNumberFilterComponent implements AgFilterComponent, OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;

	header: string | undefined;
	searchValueControl = new FormControl('', {
		validators: [Validators.pattern(/^\d*$/)] // Regex for integer validation
	});

	private model?: { value?: number };
	private params?: IFilterParams<Record<string, unknown>, unknown>;
	private readonly valueSubscription: Subscription;

	constructor() {
		this.valueSubscription = this.searchValueControl.valueChanges.pipe(
			debounceTime(500),
			distinctUntilChanged()
		).subscribe(value => {
			this.model = { value: this.parseValue(value) };
			if (this.params) {
				this.params.filterChangedCallback();
			}
		});
	}

	ngOnDestroy() {
		this.valueSubscription.unsubscribe();
	}

	agInit(params: IFilterParams<Record<string, unknown>>) {
		this.params = params;
		this.header = this.params.colDef.headerName;
	}

	isFilterActive() {
		return this.model?.value !== undefined;
	}

	doesFilterPass(filterParams: IDoesFilterPassParams<Record<string, unknown>>) {
		const value = this.getColumnValue(filterParams.node);
		return this.model?.value !== undefined ? value === this.model.value : true;
	}

	getModel() {
		return this.isFilterActive() ? { ...this.model, customFilterName: this.getCustomFilterName() } : null;
	}

	setModel(model: { value?: number } | null) {
		this.model = model !== null ? model : undefined;
		this.searchValueControl.setValue(model?.value !== undefined ? String(model.value) : '');
	}

	resetFilter() {
		this.model = undefined;
		this.searchValueControl.reset();
	}

	getCustomFilterName() {
		return FilterType.EXACT_NUMBER_FILTER;
	}

	private parseValue(value: string | null): number | undefined {
		if (!value || !/^\d*$/.test(value.trim())) {
			return undefined;
		}
		return Number(value);
	}

	private getColumnValue(node: IRowNode<Record<string, unknown>>): number | undefined {
		if (!this.params || typeof node.data !== 'object') {
			return undefined;
		}
		const { data } = node;
		if (typeof this.params.colDef.valueGetter === 'function') {
			const value = <unknown> this.params.colDef.valueGetter({
				api: this.params.api,
				colDef: this.params.colDef,
				column: this.params.column,
				context: this.params.context,
				data,
				node,
				getValue(field: string) {
					return data[field];
				}
			});
			return typeof value === 'number' ? value : undefined;
		} else if (this.params.colDef.field) {
			const value = data[this.params.colDef.field];
			return typeof value === 'number' ? value : undefined;
		}
		return undefined;
	}
}
